import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, StandardDialog, RadioField } from '@roc/ui';
import { observer } from 'mobx-react';
import { ReactNode, useEffect, useState } from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { trueFalseOptions } from '@roc/ui';
import { FileUploadButton } from '@roc/ui';
import { FormStore } from '@roc/feature-app-core';
import { ExpandMore } from '@material-ui/icons';
import { AppraisalQuestionnaireV2FormStore } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalQuestonnaireV2FormStore';
import {
  AppraisalDetailsV2Store,
  ValuationProperty,
} from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';
import { AmcSelectionCards } from './amcSelectionCards';

type Props = {
  open: any;
  onClose: any;
  onSuccess?: () => void;
};

const useStyles = makeStyles(theme => ({
  propertyCard: {
    boxShadow: 'none',
    marginBottom: '16px',
    border: '1px solid #E0E0E0',
    borderRadius: '8px !important',
    overflow: 'hidden',
    '& .MuiAccordionSummary-root': {
      backgroundColor: 'rgb(244 245 249)',
    },
  },
}));

export const AppraisalQuestionnaireModal = observer(
  ({ open, onClose, onSuccess }: Props) => {
    const { appraisalDetailsV2Store } = useStore();
    const { questionnaireFormStore, globalStore, loanStore } = appraisalDetailsV2Store;

    useEffect(() => {
      questionnaireFormStore.getAmcOptions();
    }, []);

    const handleSubmit = async () => {
      await questionnaireFormStore.onFormSubmit(loanStore.loanDetails?.properties, () => {
        if(questionnaireFormStore.form.fields.providingOwnAppraisal.value === false) {
            appraisalDetailsV2Store.setShowPaymentLinkModal(true);
        }
      });
      onSuccess ? onSuccess() : onClose();
    };

    const dialogContent = (
      <AppraisalQuestionnaire
        properties={loanStore.loanDetails?.properties}
        questionnaireFormStore={questionnaireFormStore}
        otherAmcAllowed={globalStore.userFeatures?.otherAmcAllowed}
      />
    );

    const dialogActions = (
      <>
        <Button
          testId="cancel-questionnaire"
          color="primary"
          variant="outlined"
          style={{ marginRight: '8px' }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          testId="submit-questionnaire"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        title="Valuation Questionnaire"
        subTitle={
          <Typography variant="body2" color="textSecondary">
            Please fill out the questions and upload the valuation documents, if
            needed
          </Typography>
        }
        maxWidth="sm"
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        removeContentBox
      />
    );
  }
);

export const AppraisalQuestionnaire = observer(
  ({
    properties,
    questionnaireFormStore,
    otherAmcAllowed,
    loanQuoteId,
  }: {
    properties: ValuationProperty[];
    questionnaireFormStore: AppraisalQuestionnaireV2FormStore;
    otherAmcAllowed?: boolean;
    loanQuoteId?: number;
  }) => {

    const { globalStore } = questionnaireFormStore

    const providingOwnAppraisal = questionnaireFormStore.getValue(
      'providingOwnAppraisal'
    );

    useEffect(() => {
      if (!otherAmcAllowed && !globalStore.isInternalPortal) {
        questionnaireFormStore.onFieldChange('providingOwnAppraisal', false);
      }
    }, [otherAmcAllowed]);

    return (
      <Grid container spacing={1} style={{ padding: '8px' }}>
        {(otherAmcAllowed || globalStore.isInternalPortal) && (
          <Grid item xs={12}>
            <RadioField
              label="Are you providing your own Valuation?"
              value={
                questionnaireFormStore.form.fields.providingOwnAppraisal.value
              }
              onChange={value =>
                questionnaireFormStore.onFieldChange('providingOwnAppraisal', value == 'true')
              }
              options={trueFalseOptions}
              row
              testId="providing-own-appraisal"
            />
          </Grid>
        )}

        {providingOwnAppraisal === true && (
          <>
            {(properties || []).map(property => (
              <Grid item xs={12} key={property.propertyId}>
                <PropertyAccordion
                  key={property.propertyId}
                  property={property}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FileUploadButton
                        formats=".pdf"
                        label={
                          <Typography>
                            Click here to upload the Valuation PDF document
                          </Typography>
                        }
                        onRemove={() => {
                          questionnaireFormStore.setPropertyPdfUpload(property.uuid ?? property?.propertyId, null);
                        }}
                        onUpload={file => {
                          questionnaireFormStore.setPropertyPdfUpload(property.uuid ?? property?.propertyId, file);
                          if (loanQuoteId && property.uuid) {
                            questionnaireFormStore.uploadValuationReport(file, 'pdf', loanQuoteId, property.uuid);
                          }
                        }}
                        uploadedFile={questionnaireFormStore.pdfUploads[property.uuid ?? property?.propertyId]}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FileUploadButton
                        formats=".xml"
                        label={
                          <Typography>
                            Click here to upload the Valuation XML document
                          </Typography>
                        }
                        onRemove={() => {
                          questionnaireFormStore.setPropertyXmlUpload(property.uuid ?? property?.propertyId, null);
                        }}
                        onUpload={file => {
                          questionnaireFormStore.setPropertyXmlUpload(property.uuid ?? property?.propertyId, file);
                          if (loanQuoteId && property.uuid) {
                            questionnaireFormStore.uploadValuationReport(file, 'xml', loanQuoteId, property.uuid);
                          }
                        }}
                        uploadedFile={questionnaireFormStore.xmlUploads[property.uuid ?? property?.propertyId]}
                      />
                    </Grid>
                  </Grid>
                </PropertyAccordion>
              </Grid>
            ))}
          </>
        )}
        {questionnaireFormStore.showAMCDropdown && (
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Please select the AMC for this loan
            </Typography>
            <AmcSelectionCards
              options={questionnaireFormStore.amcOptions}
              value={questionnaireFormStore.form.fields.selectedAmc.value}
              onChange={value =>
                questionnaireFormStore.onFieldChange('selectedAmc', value)
              }
            />
          </Grid>
        )}
      </Grid>
    );
  }
);

export const PropertyAccordion = ({
  property,
  children,
}: {
  property: ValuationProperty;
  children: ReactNode;
}) => {
  const classes = useStyles();
  const defaultExpanded = true;

  return (
    <Accordion
      className={classes.propertyCard}
      defaultExpanded={defaultExpanded}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box width="100%">
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {property.propertyAddress || property.address}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box flex={1} pt={2} pb={1} px={1}>
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
